import React, { useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { blockTypePlugin } from '@atlaskit/editor-plugins/block-type';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { saveOnEnterPlugin } from '@atlaskit/editor-plugins/save-on-enter';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';

import { useAIGeneratedCoverImage } from '../../hooks/useAIGeneratedCoverImage';
import { IMAGE_GENERATION_THEMES, type ImageGenerationTheme } from '../../types';

export type AIGeneratedMediaState = {
	collection: string | undefined;
	dimensions?: { height: number; width: number };
	fileMimeType?: string;
	id: string | undefined;
};

export type ImageFromAIGenerationProps = {
	/**
	 * Callback to close the popup/container after insertion.
	 */
	closePopup: () => void;
	/**
	 * Callback to insert the generated media into the editor.
	 */
	insertMedia: (mediaState: AIGeneratedMediaState) => void;
	/**
	 * The media upload collection identifier.
	 * Must have an uploadParams.collection set.
	 */
	mediaUploadCollection: string | undefined;
};

const i18n = defineMessages({
	createImageLabel: {
		id: 'ai-image-generation.image-from-ai-generation.create-image-label',
		defaultMessage: 'Create an image with Rovo',
		description: 'Label text for the Rovo image generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.image-from-ai-generation.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alternative text for the Rovo icon in the AI image generation interface.',
	},
	describeImagePrompt: {
		id: 'ai-image-generation.image-from-ai-generation.describe-image-prompt',
		defaultMessage: 'Describe an image to create',
		description: 'Prompt text for AI image generation',
	},
	describeImagePlaceholder: {
		id: 'ai-image-generation.image-from-ai-generation.describe-image-placeholder',
		defaultMessage: 'Describe an image...',
		description: 'Placeholder text for the image generation prompt editor',
	},
	generateImage: {
		id: 'ai-image-generation.image-from-ai-generation.generate-image',
		defaultMessage: 'Generate image',
		description: 'Button text for generating an image through AI',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.image-from-ai-generation.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Label for the style/theme picker dropdown',
	},
	pickRatioLabel: {
		id: 'ai-image-generation.image-from-ai-generation.pick-ratio-label',
		defaultMessage: 'Ratio',
		description: 'Label for the ratio picker dropdown',
	},
	stylePhotorealistic: {
		id: 'ai-image-generation.image-from-ai-generation.style-photorealistic',
		defaultMessage: 'Photorealistic',
		description:
			'Style option in the image generation dropdown that creates realistic photograph-like images.',
	},
	styleAbstract: {
		id: 'ai-image-generation.image-from-ai-generation.style-abstract',
		defaultMessage: 'Abstract',
		description:
			'Style option in the image generation dropdown that creates artistic images with geometric shapes and creative interpretations.',
	},
	styleWatercolors: {
		id: 'ai-image-generation.image-from-ai-generation.style-watercolors',
		defaultMessage: 'Watercolors',
		description:
			'Style option in the image generation dropdown that creates watercolor painting-style images with soft, flowing colors.',
	},
	styleGraphicDesign: {
		id: 'ai-image-generation.image-from-ai-generation.style-graphic-design',
		defaultMessage: 'Graphic Design',
		description:
			'Style option in the image generation dropdown that creates modern design images with clean layouts and bold typography.',
	},
	styleLandscape: {
		id: 'ai-image-generation.image-from-ai-generation.style-landscape',
		defaultMessage: 'Landscape',
		description:
			'Style option in the image generation dropdown that creates landscape images featuring natural or urban scenery.',
	},
	aspectRatioLabel: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-label',
		defaultMessage: 'Select a ratio',
		description: 'Label for the aspect ratio picker dropdown in AI image generation.',
	},
	aspectRatioAuto: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-auto',
		defaultMessage: 'Auto',
		description: 'Aspect ratio option that uses the default ratio determined by the AI model.',
	},
	aspectRatioWide: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-wide',
		defaultMessage: 'Wide 16:9',
		description: 'Aspect ratio option for wide images with a 16:9 ratio.',
	},
	aspectRatioLandscape: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-landscape',
		defaultMessage: 'Landscape 3:2',
		description: 'Aspect ratio option for landscape images with a 3:2 ratio.',
	},
	aspectRatioSquare: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-square',
		defaultMessage: 'Square 1:1',
		description: 'Aspect ratio option for square images with a 1:1 ratio.',
	},
	aspectRatioPortrait: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-portrait',
		defaultMessage: 'Portrait 2:3',
		description: 'Aspect ratio option for portrait images with a 2:3 ratio.',
	},
	aspectRatioNarrow: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-narrow',
		defaultMessage: 'Narrow 9:16',
		description: 'Aspect ratio option for narrow images with a 9:16 ratio.',
	},
	footerMessage: {
		id: 'ai-image-generation.image-from-ai-generation.footer-message',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Footer disclaimer message informing users that results are AI-generated',
	},
});

const DEFAULT_DIMENSIONS = { width: 760, height: 333 };

/**
 * Hard-coded mapping from aspect ratio string to the image dimensions that
 * should be inserted into the document. These values are tuned to fit the
 * editor's container/layout for each ratio, so they are intentionally not
 * derived from a single scaling formula.
 */
const ASPECT_RATIO_DIMENSIONS: Record<string, { height: number; width: number }> = {
	'16:9': { width: 702, height: 395 },
	'3:2': { width: 623, height: 415 },
	'1:1': { width: 440, height: 440 },
	'2:3': { width: 309, height: 464 },
	'9:16': { width: 278, height: 487 },
};

/**
 * Returns the image dimensions to use for a given aspect ratio string
 * (e.g. "16:9"). When no aspect ratio is provided (i.e. "Auto") or the value
 * is not in the supported list, the default 21:9 dimensions of 1260x540 are
 * returned.
 */
export function getDimensionsForAspectRatio(aspectRatio?: string | null): {
	height: number;
	width: number;
} {
	if (!aspectRatio) {
		return DEFAULT_DIMENSIONS;
	}

	return ASPECT_RATIO_DIMENSIONS[aspectRatio] ?? DEFAULT_DIMENSIONS;
}

const styles = cssMap({
	rovoIcon: {
		width: '16px',
		height: '16px',
		marginLeft: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	headerWrapper: {
		marginBottom: token('space.150'),
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.150'),
		marginBlockStart: token('space.150'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
	actionsRow: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	ratioPickerContainer: {
		paddingBottom: token('space.100'),
	},
	footer: {
		color: token('color.text.subtlest'),
		paddingBlockStart: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
	},
});

export function ImageFromAIGeneration({
	mediaUploadCollection,
	insertMedia,
	closePopup,
}: ImageFromAIGenerationProps): React.JSX.Element {
	const intl = useIntl();

	const [selectedTheme, setSelectedTheme] = useState<{
		label: string;
		value: ImageGenerationTheme;
	} | null>(null);

	const [selectedAspectRatio, setSelectedAspectRatio] = useState<{
		label: string;
		value: string | null;
	} | null>(null);

	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

	const themeOptions = [
		{
			label: intl.formatMessage(i18n.stylePhotorealistic),
			value: IMAGE_GENERATION_THEMES.photorealistic,
		},
		{ label: intl.formatMessage(i18n.styleAbstract), value: IMAGE_GENERATION_THEMES.abstract },
		{
			label: intl.formatMessage(i18n.styleWatercolors),
			value: IMAGE_GENERATION_THEMES.watercolors,
		},
		{
			label: intl.formatMessage(i18n.styleGraphicDesign),
			value: IMAGE_GENERATION_THEMES.graphic_design,
		},
		{
			label: intl.formatMessage(i18n.styleLandscape),
			value: IMAGE_GENERATION_THEMES.landscape,
		},
	];

	const aspectRatioOptions: Array<{ label: string; value: string | null }> = [
		{ label: intl.formatMessage(i18n.aspectRatioAuto), value: null },
		{ label: intl.formatMessage(i18n.aspectRatioWide), value: '16:9' },
		{ label: intl.formatMessage(i18n.aspectRatioLandscape), value: '3:2' },
		{ label: intl.formatMessage(i18n.aspectRatioSquare), value: '1:1' },
		{ label: intl.formatMessage(i18n.aspectRatioPortrait), value: '2:3' },
		{ label: intl.formatMessage(i18n.aspectRatioNarrow), value: '9:16' },
	];

	const contentId = mediaUploadCollection?.match(/\d+$/)?.[0] ?? '';
	const { generateImage, aiHeaderImageStatus, aiHeaderImageFileId } = useAIGeneratedCoverImage({
		contentId,
	});

	const isLoading = aiHeaderImageStatus === 'LOADING';

	const textRef = useRef<string>('');
	const textSerializerRef = useRef<ReturnType<typeof TextSerializer.fromSchema> | null>(null);

	const handleSubmit = useCallback(
		(prompt: string) => {
			if (!prompt.trim()) {
				return;
			}
			const useRawPrompt = !selectedTheme?.value;

			generateImage({
				adfContent: prompt,
				useRawPrompt,
				...(selectedTheme?.value && { theme: selectedTheme.value }),
				...(selectedAspectRatio?.value && { aspectRatio: selectedAspectRatio.value }),
			});
		},
		[generateImage, selectedTheme, selectedAspectRatio],
	);

	const handleSubmitRef = useRef(handleSubmit);
	useEffect(() => {
		handleSubmitRef.current = handleSubmit;
	}, [handleSubmit]);

	const getTextSerializer = useCallback((editorView: EditorView) => {
		if (!textSerializerRef.current) {
			textSerializerRef.current = TextSerializer.fromSchema(editorView.state.schema);
		}
		return textSerializerRef.current;
	}, []);

	const onSave = useCallback(
		(editorView: EditorView) => {
			const text = getTextSerializer(editorView).serializeFragment(editorView.state.doc.content);
			handleSubmitRef.current(text);
		},
		[getTextSerializer],
	);

	const handleEditorChange = useCallback(
		(editorView: EditorView) => {
			const text = getTextSerializer(editorView).serializeFragment(editorView.state.doc.content);
			textRef.current = text;
			setIsSubmitDisabled(!text.trim());
		},
		[getTextSerializer],
	);

	const handleSubmitClick = useCallback(() => {
		handleSubmit(textRef.current);
	}, [handleSubmit]);

	const { preset } = usePreset(
		(builder) =>
			builder
				.add([basePlugin, {}])
				.add(compositionPlugin)
				.add(focusPlugin)
				.add(betterTypeHistoryPlugin)
				.add([pastePlugin, {}])
				.add(clipboardPlugin)
				.add(typeAheadPlugin)
				.add([
					placeholderPlugin,
					{ placeholder: intl.formatMessage(i18n.describeImagePlaceholder) },
				])
				.add([blockTypePlugin, { allowBlockType: { exclude: ['heading', 'blockquote'] } }])
				.add([saveOnEnterPlugin, onSave]),
		[onSave, intl],
	);

	// Insert generated image into editor once available
	useEffect(() => {
		if (aiHeaderImageStatus === 'NOT_LOADING' && aiHeaderImageFileId) {
			insertMedia({
				id: aiHeaderImageFileId,
				collection: mediaUploadCollection,
				fileMimeType: 'image/png',
				dimensions: getDimensionsForAspectRatio(selectedAspectRatio?.value),
			});
			closePopup();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aiHeaderImageStatus, aiHeaderImageFileId]);

	return (
		<EditorContext>
			<Stack grow="fill">
				<Box xcss={styles.headerWrapper}>
					<Inline space="space.100" alignBlock="center">
						<Box xcss={styles.rovoIcon}>
							<RovoIcon
								appearance="brand"
								size="xsmall"
								label={intl.formatMessage(i18n.rovoIconAlt)}
							/>
						</Box>
						<Text size="medium" weight="bold">
							{intl.formatMessage(i18n.createImageLabel)}
						</Text>
					</Inline>
				</Box>

				{fg('m0_editor_image_generation_exclusion') && (
					<Select
						inputId="image-generation-theme-picker"
						aria-label={intl.formatMessage(i18n.pickStyleLabel)}
						options={themeOptions}
						value={selectedTheme}
						onChange={(newValue) => setSelectedTheme(newValue)}
						placeholder={intl.formatMessage(i18n.pickStyleLabel)}
						isDisabled={isLoading}
						styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
						menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
					/>
				)}

				<Box xcss={styles.ratioPickerContainer}>
					<Text>{intl.formatMessage(i18n.pickRatioLabel)}</Text>
				</Box>
				<Select
					inputId="image-generation-aspect-ratio-picker"
					aria-label={intl.formatMessage(i18n.aspectRatioLabel)}
					options={aspectRatioOptions}
					value={selectedAspectRatio}
					onChange={(newValue) => setSelectedAspectRatio(newValue)}
					placeholder={intl.formatMessage(i18n.aspectRatioLabel)}
					isDisabled={isLoading}
					styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
					menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				/>

				<Box xcss={styles.imagePromptContainer}>
					<Box xcss={styles.stack}>
						<ComposableEditor
							appearance="chromeless"
							preset={preset}
							shouldFocus
							disabled={isLoading}
							onChange={handleEditorChange}
							assistiveLabel={intl.formatMessage(i18n.describeImagePlaceholder)}
						/>
						<Box xcss={styles.actionsRow}>
							<IconButton
								testId="image-generation-submit"
								appearance="primary"
								icon={ArrowUpIcon}
								label={intl.formatMessage(i18n.generateImage)}
								isLoading={isLoading}
								isDisabled={isSubmitDisabled || isLoading}
								onClick={handleSubmitClick}
							/>
						</Box>
					</Box>
				</Box>

				<Box xcss={styles.footer}>
					<Inline space="space.050" alignBlock="center">
						<InformationCircleIcon color="currentColor" label="" size="small" />
						<Link
							appearance="subtle"
							href="https://www.atlassian.com/trust/atlassian-intelligence"
							target="_blank"
						>
							<Text size="small" color="color.text.subtlest">
								{intl.formatMessage(i18n.footerMessage)}
							</Text>
						</Link>
					</Inline>
				</Box>
			</Stack>
		</EditorContext>
	);
}
